import { DimensionValue, ScrollView, StyleSheet, View } from "react-native";
import { useMemo } from "react";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import useDashboardStats from "@/hooks/useDashboardStats";
import type { StatusItem } from "@/services/dashboardService";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

type Stat = {
  title: string;
  total: number;
  statuses: StatusItem[];
  accent?: string;
};

export default function Dashboard() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const { hasProjectsAccess } = usePermissions();
  const { users, clients, projects, invoices, projectsSnapshot } = useDashboardStats();
  useSetPageTitle("Dashboard");

  const stats = useMemo(() => {
    const items: Stat[] = [];
    if (users) {
      items.push({
        title: displayNames?.users?.plural || "Users",
        total: users.total,
        statuses: users.statuses,
        accent: "#3B82F6",
      });
    }
    if (clients) {
      items.push({
        title: displayNames?.clients?.plural || "Clients",
        total: clients.total,
        statuses: clients.statuses,
        accent: "#06B6D4",
      });
    }
    if (projects) {
      items.push({
        title: displayNames?.projects?.plural || "Projects",
        total: projects.total,
        statuses: projects.statuses,
        accent: "#22C55E",
      });
    }
    if (invoices) {
      items.push({
        title: displayNames?.invoices?.plural || "Invoices",
        total: invoices.total,
        statuses: invoices.statuses,
        accent: "#8B5CF6",
      });
    }
    return items;
  }, [users, clients, projects, invoices, displayNames]);

  // Billing mock
  const estimated = 128_000;
  const completed = 94_000;
  const remaining = Math.max(estimated - completed, 0);
  const pct = useMemo(
    () => (estimated ? Math.min(100, Math.round((completed / estimated) * 100)) : 0),
    [estimated, completed],
  );

  const money = (n: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

  const projectsLabel = displayNames?.projects?.plural || "Projects";

  return (
    <ScrollView
      contentContainerStyle={{ gap: 16 }}
      style={[s.container, { backgroundColor: colors.backgroundSecondary }]}
    >
      {/* KPI GRID */}
      {stats.length > 0 && (
        <View style={s.grid}>
          {stats.map((st) => (
            <KpiCard key={st.title} stat={st} border={colors.border} card={colors.card} text={colors.text} />
          ))}
        </View>
      )}

      {/* BILLING */}
      <View style={[s.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <View style={s.sectionHeader}>
          <ThemedText type="subtitle">Billing</ThemedText>
          <ThemedText type="default">{pct}% complete</ThemedText>
        </View>

        <ProgressBar percent={pct} />

        <View style={s.rowSpread}>
          <LabelValue label="Estimated" value={money(estimated)} />
          <LabelValue label="Completed" value={money(completed)} />
          <LabelValue label="Remaining" value={money(remaining)} />
        </View>
      </View>

      {/* PROJECTS SNAPSHOT */}
      {hasProjectsAccess && projectsSnapshot.length > 0 && (
        <View style={[s.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={s.sectionHeader}>
            <ThemedText type="subtitle">{projectsLabel} Snapshot</ThemedText>
            <ThemedText type="default">Top 3 In Progress</ThemedText>
          </View>
          <MiniList
            items={projectsSnapshot.map((p, i) => ({
              key: p.id,
              name: p.name,
              metric: `${p.shiftCount} ${p.shiftCount === 1 ? displayNames?.shifts?.name : displayNames?.shifts?.plural}`,
              accent: ["#22C55E", "#3B82F6", "#F59E0B"][i] ?? "#22C55E",
            }))}
          />
        </View>
      )}
    </ScrollView>
  );
}

/* ---------- Components ---------- */

function KpiCard({ stat, border, card, text }: { stat: Stat; border: string; card: string; text: string }) {
  const { colors } = useTheme();
  return (
    <View style={[s.card, { borderColor: border, backgroundColor: card }]}>
      <View style={s.cardHeader}>
        <AccentDot color={stat.accent ?? text} />
        <ThemedText type="subtitle">{stat.title}</ThemedText>
      </View>

      <ThemedText type="title">{formatNumber(stat.total)}</ThemedText>

      {stat.statuses.length > 0 && (
        <View style={{ gap: 6 }}>
          {stat.statuses.map((st) => {
            const themed = colors[st.color as keyof typeof colors];
            const dotColor = themed && typeof themed === "object" && "border" in themed ? themed.border : text;
            return (
              <View key={st.status} style={s.statusRow}>
                <View style={[s.statusDot, { backgroundColor: dotColor }]} />
                <ThemedText style={s.statusLabel}>{titleCase(st.status)}</ThemedText>
                <ThemedText style={s.statusCount}>{formatNumber(st.count)}</ThemedText>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

function ProgressBar({ percent }: { percent: number }) {
  return (
    <View style={s.progressTrack}>
      <View style={[s.progressFill, { width: `${percent}%` }]} />
    </View>
  );
}

function LabelValue({ label, value }: { label: string; value: string }) {
  return (
    <View style={s.labelValue}>
      <ThemedText style={s.muted}>{label}</ThemedText>
      <ThemedText>{value}</ThemedText>
    </View>
  );
}

function MiniList({ items }: { items: { key?: string; name: string; metric: string; accent: string }[] }) {
  return (
    <View style={{ gap: 10 }}>
      {items.map((it, i) => (
        <View key={it.key ?? it.name ?? i} style={s.listRow}>
          <AccentDot color={it.accent} />
          <ThemedText style={{ flex: 1 }}>{it.name}</ThemedText>
          <ThemedText type="default">{it.metric}</ThemedText>
        </View>
      ))}
    </View>
  );
}

function AccentDot({ color }: { color: string }) {
  return <View style={[s.dot, { backgroundColor: color }]} />;
}

function titleCase(s: string) {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatNumber(n: number) {
  return new Intl.NumberFormat("en-US").format(n);
}

/* ---------- Styles ---------- */

const s = StyleSheet.create({
  container: { width: "100%", padding: 16, overflow: "scroll", paddingBottom: 24 },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  card: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 14,
    gap: 8,
    flexGrow: 1,
    flexBasis: "calc(50% - 8px)" as DimensionValue,
  },
  cardHeader: { flexDirection: "row", alignItems: "center", gap: 8 },
  statusRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  statusDot: { width: 8, height: 8, borderRadius: 999 },
  statusLabel: { flex: 1, fontSize: 13 },
  statusCount: { fontSize: 13, fontWeight: "600" },
  dot: { width: 10, height: 10, borderRadius: 999 },
  listRow: {},
  section: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 16,
    padding: 14,
    gap: 12,
  },
  sectionHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  progressTrack: {
    height: 10,
    borderRadius: 999,
    backgroundColor: "rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 999,
    backgroundColor: "#22C55E",
  },
  rowSpread: { flexDirection: "row", justifyContent: "space-between", gap: 12 },
  labelValue: { gap: 2, minWidth: 100 },
  muted: { opacity: 0.7 },
});
